import { Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import type { ColumnConfig } from '@/components/DataTable';
import { groupPoliciesPath } from '@/lib/policyPaths';
import type { SecurityGroup } from '@kirin/api-client/contracts/security-groups';
import {
  creatorLabel,
  SECURITY_GROUP_SORT_FIELDS,
} from './securityGroupsTable';

/**
 * Columns for the Security Groups table (KIR-3800).
 *
 * Name, Users and Created by are sortable; Description and Actions are not
 * (free text and controls respectively). Sorting is applied over the FULL group
 * list before pagination — see `fetchSecurityGroupsPage` — so it is never a
 * misleading sort of the current page only.
 */
export function createSecurityGroupsColumns(
  organizationId: string,
  canEdit: boolean,
  onEdit: (group: SecurityGroup) => void,
  onDelete: (group: SecurityGroup) => void
): ColumnConfig<SecurityGroup>[] {
  const columns: ColumnConfig<SecurityGroup>[] = [
    {
      id: SECURITY_GROUP_SORT_FIELDS.NAME,
      header: 'Name',
      accessor: 'name',
      sortable: true,
      sortField: SECURITY_GROUP_SORT_FIELDS.NAME,
      width: 220,
      renderCell: ({ row }) => <span className="font-medium">{row.name}</span>,
    },
    {
      id: 'description',
      header: 'Description',
      accessor: 'description',
      sortable: false,
      renderCell: ({ row }) => (
        <span className="text-muted-foreground">{row.description || '—'}</span>
      ),
    },
    {
      id: SECURITY_GROUP_SORT_FIELDS.USER_COUNT,
      header: 'Users',
      accessor: 'userCount',
      sortable: true,
      sortField: SECURITY_GROUP_SORT_FIELDS.USER_COUNT,
      width: 120,
      renderCell: ({ row }) => (
        <span className="flex items-center gap-1.5">
          <Users className="h-4 w-4 text-muted-foreground" />
          {row.userCount ?? 0}
        </span>
      ),
    },
    {
      id: SECURITY_GROUP_SORT_FIELDS.CREATED_BY,
      header: 'Created by',
      accessor: 'createdBy',
      sortable: true,
      sortField: SECURITY_GROUP_SORT_FIELDS.CREATED_BY,
      width: 200,
      renderCell: ({ row }) => {
        const label = creatorLabel(row);
        return label ? (
          <span>{label}</span>
        ) : (
          // Covers a group predating creator tracking, a deleted creator, and a
          // creator with neither name nor email. Never a raw uuid.
          <span
            className="text-muted-foreground"
            title="No creator recorded for this group"
          >
            —
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      sortable: false,
      width: 220,
      renderCell: ({ row }) => (
        // stopPropagation throughout: the whole row is a link into the group,
        // so without it every action would also navigate.
        <div
          className="flex justify-end gap-2"
          onClick={event => event.stopPropagation()}
        >
          <Button variant="outline" size="sm" asChild>
            <Link to={groupPoliciesPath(organizationId, row.id)}>Policies</Link>
          </Button>
          {canEdit ? (
            <>
              <Button variant="outline" size="sm" onClick={() => onEdit(row)}>
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={() => onDelete(row)}>
                Delete
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return columns;
}
